import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Scale, BookOpen, Building2, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { LawsStrip } from "@/components/landing/LawsStrip";
import { Testimonials } from "@/components/landing/Testimonials";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Landing() {
  const { lang } = useLang();
  const { user } = useAuth();
  const featureIcons = [Sparkles, BookOpen, Building2, Scale];

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-[#0A1128]">
      {/* NAV */}
      <header className="border-b border-[#E9ECEF] bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <span className="inline-block w-2 h-6 bg-[#C65D47]" />
            <span className="font-display text-xl">Law Pilot</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#how" className="text-[#495057] hover:text-[#0A1128]" data-testid="nav-how">{t(lang,"nav.features")}</a>
            <a href="#features" className="text-[#495057] hover:text-[#0A1128]" data-testid="nav-features">{t(lang,"nav.knowledge")}</a>
            <Link to="/pricing" className="text-[#495057] hover:text-[#0A1128]" data-testid="nav-pricing">{t(lang,"nav.pricing")}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <Link to="/dashboard" className="hidden sm:inline-flex bg-[#0A1128] text-white text-sm px-4 py-2 hover:bg-[#1C2538]" data-testid="nav-dashboard-cta">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex text-sm px-3 py-2 hover:bg-[#F1F3F5]" data-testid="nav-login">
                  {t(lang,"nav.login")}
                </Link>
                <Link to="/register" className="bg-[#0A1128] text-white text-sm px-4 py-2 hover:bg-[#1C2538]" data-testid="nav-start">
                  {t(lang,"nav.start")}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="hero-veil relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <motion.p {...fadeUp} className="text-xs tracking-[0.24em] uppercase font-bold text-[#C65D47] mb-6" data-testid="hero-kicker">
              {t(lang,"hero.kicker")}
            </motion.p>
            <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="hero-title">
              <span className="block">{t(lang,"hero.title_a")}</span>
              <span className="block italic">{t(lang,"hero.title_b")}</span>
              <span className="block">{t(lang,"hero.title_c")}</span>
            </motion.h1>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-8 max-w-2xl text-lg text-[#495057] leading-relaxed">
              {t(lang,"hero.sub")}
            </motion.p>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to={user ? "/chat" : "/register"} data-testid="hero-cta-primary" className="inline-flex items-center justify-center gap-2 bg-[#0A1128] text-white px-7 py-4 text-sm tracking-wide hover:bg-[#1C2538] transition-colors">
                {t(lang,"hero.cta_primary")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/knowledge" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center gap-2 border border-[#0A1128] text-[#0A1128] px-7 py-4 text-sm tracking-wide hover:bg-white transition-colors">
                {t(lang,"hero.cta_secondary")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }} className="mt-8 text-xs text-[#868E96] max-w-md">
              ※ {t(lang,"hero.disclaimer")}
            </motion.p>
          </div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[#E9ECEF]">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/6aa7d904-b7e4-4b7a-bd46-524404d838e7/images/8729cd92199028b967e8ab0e90b6fece9e1c5e7d5baec0d938d99a6808631edd.jpeg"
                  alt="Signing a corporate agreement at a law office"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -start-6 w-44 bg-white border border-[#E9ECEF] p-5 shadow-md">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#868E96]">Law Pilot · AI</div>
                <div className="mt-2 text-sm leading-relaxed">
                  “Under Art. 50, notice for 7 yrs of service is <span className="font-medium">3 months</span>.”
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <LawsStrip />

      {/* FEATURES */}
      <section id="features" className="border-t border-[#E9ECEF] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-5">
              <div className="editorial-rule pt-4">
                <h2 className="font-display text-4xl md:text-5xl leading-tight">{t(lang,"features.title")}</h2>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E9ECEF] border border-[#E9ECEF]">
            {t(lang,"features.items").map((it, i) => {
              const Icon = featureIcons[i] || Sparkles;
              return (
                <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                  className="bg-white p-8 md:p-10 hover:bg-[#F8F9FA] transition-colors"
                  data-testid={`feature-${i}`}
                >
                  <Icon className="w-7 h-7 text-[#C65D47]" />
                  <h3 className="mt-6 font-display text-2xl">{it.t}</h3>
                  <p className="mt-3 text-[#495057] leading-relaxed">{it.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
          <h2 className="font-display text-4xl md:text-5xl mb-16 max-w-2xl">{t(lang,"how.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t(lang,"how.steps").map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} data-testid={`how-step-${i}`}>
                <div className="text-[#C65D47] font-display text-5xl">{s.n}</div>
                <div className="mt-4 h-px bg-white/20" />
                <h3 className="mt-6 text-xl font-medium">{s.t}</h3>
                <p className="mt-3 text-white/70 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* CTA */}
      <section id="cta" className="bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-24 text-center">
          <h2 className="font-display text-4xl md:text-6xl leading-tight">{t(lang,"cta.title")}</h2>
          <div className="mt-10">
            <Link to={user ? "/dashboard" : "/register"} className="inline-flex items-center gap-2 bg-[#C65D47] text-white px-8 py-4 hover:bg-[#A94834] transition-colors" data-testid="cta-create-account">
              {t(lang,"cta.btn")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E9ECEF] bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#868E96]">
          <span>{t(lang,"footer")}</span>
          <span>{t(lang,"common.disclaimer")}</span>
        </div>
      </footer>
    </div>
  );
}
